import { NgClass } from '@angular/common';
import type { OnInit, PipeTransform } from '@angular/core';
import { ChangeDetectionStrategy, Component, Pipe, ViewEncapsulation, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormArray, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import type { ApiTypes } from '@hyundai/ng-common-lib';
import { FootnotesPipe, ServicesFeature, TrackingFeature } from '@hyundai/ng-common-lib';
import { M020C060InputCheckboxComponent, M020C070InputToggleComponent } from '@hyundai/ui';
import { Store } from '@ngrx/store';
import { CHECKOUT_STEP_TOKEN, CheckoutStepComponent } from '@pixelgenau/ngx-checkouts';

@Pipe({
    standalone: true,
    name: 'serviceFormSorter',
})
class ServiceFormSorter implements PipeTransform {
    transform(value: Array<FormGroup<ServiceFormItem>>) {
        return value.sort((el) => (el.value.selected ? -1 : 1));
    }
}

interface ServiceFormItem {
    identifier: FormControl<string>;
    name: FormControl<string>;
    selected: FormControl<boolean>;
}

export type ServiceFormListStepData = Pick<ApiTypes.ServiceFormPayload, 'services' | 'replacementVehicle'>;

@Component({
    selector: 'app-service-form-list-step',
    standalone: true,
    templateUrl: './service-form-list-step.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        M020C060InputCheckboxComponent,
        M020C070InputToggleComponent,
        FootnotesPipe,
        NgClass,
        FormsModule,
        ReactiveFormsModule,
        ServiceFormSorter,
    ],
    providers: [{ provide: CHECKOUT_STEP_TOKEN, useExisting: ServiceFormListStepComponent }],
})
export default class ServiceFormListStepComponent extends CheckoutStepComponent implements OnInit {
    protected readonly activatedRoute = inject(ActivatedRoute);
    store = inject(Store);
    servicesFormContent = this.store.selectSignal(ServicesFeature.state.selectLoadedServiceForm);
    readMore = signal(false);
    trackingStarted = signal(false);
    queryParams = toSignal(this.activatedRoute.queryParams);
    showAllServices = signal(false);

    showAll() {
        this.showAllServices.set(!this.showAllServices());
    }

    featuredServiceId = computed(() => {
        const queryParams = this.queryParams();
        const id = queryParams?.['service'] as string | undefined;
        return id?.toString();
    });

    featuredService = computed(() => {
        const servicesFormContent = this.servicesFormContent();
        const featuredServiceId = this.featuredServiceId();
        if (servicesFormContent.campaign.afterSalesServices && featuredServiceId) {
            return servicesFormContent.campaign.afterSalesServices.find((s) => s.value === featuredServiceId);
        }

        return null;
    });

    override form = new FormGroup({
        generalServices: new FormArray<FormGroup<ServiceFormItem>>([]),
        afterSalesServices: new FormArray<FormGroup<ServiceFormItem>>([]),
        replacementVehicle: new FormControl<boolean>(false, { nonNullable: true }),
    });

    formValueChanges = toSignal(this.form.valueChanges);
    thanksValue = computed(() => {
        const formValue = this.formValueChanges();
        if (!formValue) {
            return [];
        }

        const thanksValue: string[] = [];
        const generalServices = formValue.generalServices;
        const afterSalesServices = formValue.afterSalesServices;

        thanksValue.push(
            ...this.getSelectedValuesFromControl(generalServices || []),
            ...this.getSelectedValuesFromControl(afterSalesServices || []),
        );
        return thanksValue;
    });

    formIsValid = computed(() => {
        return this.thanksValue().length > 0;
    });

    ngOnInit(): void {
        const generalServices = this.servicesFormContent().generalServices;
        if (generalServices && generalServices.length > 0) {
            this.addCheckboxes(generalServices, this.form.controls.generalServices as FormArray);
        }

        const afterSalesServices = this.servicesFormContent().campaign.afterSalesServices;
        if (afterSalesServices && afterSalesServices.length > 0) {
            this.addCheckboxes(afterSalesServices, this.form.controls.afterSalesServices as FormArray, true);
        }
    }

    private addCheckboxes(
        services: Array<{
            value: string;
            label: string | null | undefined;
            trackingName: string | null | undefined;
            price?: string;
            priceLabel?: string | null;
            serviceId?: string | null;
        }>,
        formArray: FormArray,
        filterWithOutServiceId = false,
    ): void {
        for (const element of services) {
            if (element.serviceId || !filterWithOutServiceId) {
                const formGroup = new FormGroup({
                    identifier: new FormControl(element.value, { nonNullable: true }),
                    name: new FormControl(
                        `${element.label}${element.price ? ` - ${element.priceLabel ?? ''} ${element.price}` : ''}`,
                        {
                            nonNullable: true,
                        },
                    ),
                    selected: new FormControl(this.featuredServiceId() === element.value ? true : false, {
                        nonNullable: true,
                    }),
                    trackingName: new FormControl(element.trackingName, { nonNullable: true }),
                });
                formArray.push(formGroup);
            }
        }
    }

    private getSelectedValuesFromControl(
        controlValue: Array<Partial<{ identifier: string; name: string; selected: boolean; trackingName: string }>>,
    ): string[] {
        const selectedValues: string[] = [];

        if (controlValue && Array.isArray(controlValue)) {
            for (const item of controlValue) {
                if (item.selected && item.name) {
                    selectedValues.push(item.name);
                }
            }
        }

        return selectedValues;
    }

    private getTrackingValuesFromControl(
        controlValue: Array<Partial<{ identifier: string; name: string; selected: boolean; trackingName: string }>>,
    ): string[] {
        const trackingValues: string[] = [];

        if (controlValue && Array.isArray(controlValue)) {
            for (const item of controlValue) {
                if (item.selected && item.name && item.trackingName) {
                    trackingValues.push(item.trackingName);
                }
            }
        }

        return trackingValues;
    }

    trackCheckbox(): void {
        if (!this.trackingStarted()) {
            this.store.dispatch(
                TrackingFeature.actions.eventTrackingPush({ dataLayer: { event: 'service_request_start' } }),
            );
            this.trackingStarted.set(true);
        }
    }

    next(): void {
        const formValue = this.formValueChanges();
        if (formValue) {
            const generalServices = formValue.generalServices;
            const afterSalesServices = formValue.afterSalesServices;

            const trackingValue = [
                ...this.getTrackingValuesFromControl(generalServices || []),
                ...this.getTrackingValuesFromControl(afterSalesServices || []),
            ];

            this.store.dispatch(
                TrackingFeature.actions.eventTrackingPush({
                    dataLayer: {
                        event: 'service_request_select_services',
                        serviceRequest: {
                            services: trackingValue.join(', '),
                        },
                    },
                }),
            );
        }

        super.complete();
    }

    override showStepperMenu(): boolean {
        return false;
    }

    override showSidebar(): boolean {
        return false;
    }

    override getSubmitData(): ServiceFormListStepData {
        const ret: ServiceFormListStepData = {
            services: this.thanksValue(),
            replacementVehicle: this.form.value.replacementVehicle ?? false,
        };
        return ret;
    }
}
